import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  QueryList,
  ViewChild,
  ViewChildren,
  OnDestroy
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';

import { multiSelectRangeValidator } from '../../../../custom-validator/multi-select.range.validator';
import { ValuePair } from '../../../../interfaces/ValuePair';
import { BaseSelectComponent } from '../../base-select.component';
import { BaseSelect } from '../../base-select.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ls-multi-select',
  templateUrl: './ls-multi-select.component.html',
  styleUrls: ['./ls-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LsMultiSelectComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LsMultiSelectComponent),
      multi: true
    }
  ]
})
export class LsMultiSelectComponent extends BaseSelectComponent
  implements OnChanges, BaseSelect, AfterViewInit, OnDestroy {
  public selectedData: Array<string>;
  @Input()
  public multiSelectSearch = false;
  @Input()
  public selectAll = false;
  // tslint:disable-next-line:no-input-rename
  @Input('min')
  private minSelected: number;
  // tslint:disable-next-line:no-input-rename
  @Input('max')
  private maxSelected: number;

  @ViewChild('selectSearch')
  private selectSearch: ElementRef;
  @ViewChildren('listItem')
  private listItems: QueryList<ElementRef>;
  private onDestroy$: Subject<void> = new Subject();

  constructor() {
    super();
  }

  public getItems(): Array<ValuePair> {
    if (this.multiSelectSearch && this.selectSearch) {
      let filter: string = this.selectSearch.nativeElement.value;
      if (filter) {
        filter = filter.toLowerCase();
        return this.items.filter((item: ValuePair) => {
          const name: any = item.name.toLowerCase();
          const value: string =
            typeof item.value === 'string' || item.value instanceof String
              ? item.value.toLowerCase()
              : item.value.toString().toLowerCase();
          return (
            name.includes(filter) ||
            value.includes(filter) ||
            this.isSelected(item)
          );
        });
      } else {
        return this.items;
      }
    } else {
      return this.items;
    }
  }

  public isSelected(item: ValuePair): boolean {
    if (this.selectedData) {
      return (
        this.selectedData.filter((value: string) => {
          return value === item.value;
        }).length > 0
      );
    }
    return false;
  }

  public pillCount(): number {
    return this.selectedData ? this.selectedData.length : 0;
  }

  public writeValue(obj: any): void {
    if (Array.isArray(obj)) {
      this.selectedData = obj;
    } else if (obj) {
      throw Error('ls-multi-select only accepts arrays as data');
    }
  }

  public ngOnChanges(changes: any): void {
    if (changes.minSelected != null || changes.maxSelected != null) {
      this.validateFn = multiSelectRangeValidator(
        this.minSelected,
        this.maxSelected
      );
    } else {
      this.validateFn = multiSelectRangeValidator(null, null);
    }
  }

  public ngAfterViewInit(): void {
    this.listItems.changes
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((items: QueryList<ElementRef>) => {
        if (items && items.length && !this.multiSelectSearch) {
          this.listItems.first.nativeElement.focus();
        }
      });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public select(item: ValuePair, event: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    let index = -1;
    if (this.selectedData) {
      index = this.selectedData.indexOf(item.value);
    }
    if (index > -1) {
      this.selectedData.splice(index, 1);
      if (this.selectedData.length === 0) {
        this.selectedData = null;
      }
    } else {
      if (!this.selectedData) {
        this.selectedData = [];
      }
      this.selectedData.push(item.value);
    }
    this.propagateChange(this.selectedData);
  }

  public selectAllItems(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedData = this.getItems().map(item => item.value);
    this.propagateChange(this.selectedData);
  }

  public open(event: MouseEvent): void {
    this.panelOpen = true;
    this.onOpen();
  }
}
